var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { styled, Tooltip, tooltipClasses, Typography } from "@mui/material";
const StyledClampedTypography = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "lines" && prop !== "lineHeight",
})(({ lines, lineHeight }) => ({
    lineHeight: lineHeight ? (typeof lineHeight === "number" ? `${lineHeight * 1.6}rem` : lineHeight) : undefined,
    display: "-webkit-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    WebkitBoxOrient: "vertical",
    boxSizing: "border-box",
    WebkitLineClamp: lines,
    // Padding to handle some font quirks.
    padding: "0 0.15rem",
}));
const CustomWidthTooltip = styled((_a) => {
    var { className, maxWidth } = _a, props = __rest(_a, ["className", "maxWidth"]);
    return (_jsx(Tooltip, Object.assign({}, props, { classes: { popper: className } })));
})(({ maxWidth }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: maxWidth,
    },
}));
export const ClampedTypography = (_a) => {
    var { lines = 2, lineHeight, tooltip = false, tooltipProps = {}, tooltipMaxWidth = "none", children } = _a, typographyProps = __rest(_a, ["lines", "lineHeight", "tooltip", "tooltipProps", "tooltipMaxWidth", "children"]);
    const textRef = useRef(null);
    const [isOverflowed, setIsOverflowed] = useState(false);
    // Check vertical overflow (for multi-line clamping)
    const checkOverflow = useCallback(() => {
        if (textRef.current) {
            const { scrollHeight, clientHeight } = textRef.current;
            setIsOverflowed(scrollHeight > clientHeight);
        }
    }, []);
    useLayoutEffect(() => {
        const node = textRef.current;
        if (!node)
            return;
        const resizeObserver = new ResizeObserver(() => {
            window.requestAnimationFrame(checkOverflow);
        });
        resizeObserver.observe(node);
        checkOverflow();
        return () => {
            resizeObserver.disconnect();
        };
    }, [checkOverflow]);
    // Re-check if the children (text content) changes.
    useEffect(() => {
        checkOverflow();
    }, [children, checkOverflow]);
    const content = (_jsx(StyledClampedTypography, Object.assign({ ref: textRef, lines: lines, lineHeight: lineHeight }, typographyProps, { children: children })));
    if (tooltip) {
        return (_jsx(CustomWidthTooltip, Object.assign({ title: isOverflowed ? children : "", maxWidth: tooltipMaxWidth, disableHoverListener: !isOverflowed, placement: "top" }, tooltipProps, { children: content })));
    }
    return content;
};
